'use client';
import React from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { Economics, ISetParams } from '@benzinga/calendar-manager';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';
import { fetchEconomics } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';

import { ICalendarProps, IDateRange } from '../lib/interfaces';

export const EconomicsColDef = ColumnsDefinition;
export const fetchEconomicsData = fetchEconomics;

export const EconomicsCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  initialData,
  initialDateRange,
  leftSlot,
  onReady,
}) => {
  const session = React.useContext(SessionContext);
  const dateRange: IDateRange = initialDateRange ?? {
    date_from: formatRequestDate(DateTime.now().minus({ days: 7 }).toJSDate()),
    date_to: formatRequestDate(new Date()),
  };

  const initialParams = React.useMemo(
    () => ({
      dateFrom: dateRange.date_from ?? '',
      dateTo: dateRange.date_to ?? '',
    }),
    [dateRange.date_from, dateRange.date_to],
  );

  const { loading, onFilterChanged, onViewportChanged, setTableData, tableData } = useCalendarPagedFetch({
    fetchPage: (params, page) =>
      fetchEconomicsData(session.getSession(), {
        ...(params as ISetParams),
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
      }),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setTableData]);

  function onTableReady() {
    if (initialData) {
      setTableData(initialData, initialParams);
    } else {
      onFilterChanged(initialParams);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      enableSearch={false}
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      initialDateRange={dateRange}
      leftSlot={leftSlot}
      onFilterChanged={onFilterChanged}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      viewportChange={onViewportChanged}
    />
  );
};
